import { ApprovalType, LeaveRequestDTO } from "@/models/leave-requests.model";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { formatDateLocale } from "@/utils/date-format";
import ApprovalTypeTag from "../../../../components/tags/ApprovalTypeTag";
import { getSignedUrl } from "@/api/leave-requests";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch } from "@/app/hooks";
import {
  approveOrRejectLeaveRequest,
  fetchAllLeaveRequestsCount,
  fetchLeaveRequests
} from "@/features/leaveRequests/leaveRequestSlice";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { Separator } from "@/components/ui/separator";
import UserSnippet from "@/components/user/UserSnippet";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { IoOpenOutline } from "react-icons/io5";

interface Props {
  requestObj: LeaveRequestDTO;
}

function ApprovalSheet({ requestObj }: Props) {
  const dispatch = useAppDispatch();
  const [request, setRequest] = useState<LeaveRequestDTO>(requestObj);
  const [imageSignedUrl, setImageSignedUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    async function fetchSignedUrl() {
      console.log("Fetching signed URL for:", requestObj.fileUrl);
      if (!requestObj.fileUrl) return;
      const { signedUrl } = await getSignedUrl(requestObj.fileUrl);
      setImageSignedUrl(signedUrl);
      const fileExtension = requestObj.fileUrl?.split('.').pop()?.toLowerCase();
      const isPdf = fileExtension === 'pdf';
      setIsPdf(isPdf);
    }

    // Update local state when props change
    setRequest(requestObj);

    // Only fetch signed URL when sheet is open
    if (isOpen) {
      fetchSignedUrl();
    }
  }, [requestObj, isOpen]);

  async function handleApprove() {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      // Dispatch the action and wait for it to complete
      await dispatch(
        approveOrRejectLeaveRequest({
          isApproved: true,
          leaveRequestId: request.id
        })
      ).unwrap();

      // Update local state
      setRequest(prev => ({ ...prev, status: ApprovalType.APPROVED }));

      // Show success toast
      toast({
        title: "Approval successful!",
        description: `Leave request approved for ${request.user.fullName}`,
      });

      // Refresh the main leave requests list
      await dispatch(fetchLeaveRequests({}));
      await dispatch(fetchAllLeaveRequestsCount())

      // Close the sheet after success (optional)
      setIsOpen(false);
    } catch (error: any) {
      console.error("Caught error:", error);

      toast({
        variant: "destructive",
        title: error.message || "Error",
        description: error.error || "An unknown error occurred",
      })
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleReject() {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      // Dispatch the action and wait for it to complete
      await dispatch(
        approveOrRejectLeaveRequest({
          isApproved: false,
          leaveRequestId: request.id
        })
      ).unwrap();

      // Update local state
      setRequest(prev => ({ ...prev, status: ApprovalType.REJECTED }));

      // Show success toast
      toast({
        title: "Rejection successful!",
        description: `Leave request rejected for ${request.user.fullName}`,
      });

      // Refresh the main leave requests list
      await dispatch(fetchLeaveRequests({
      }));
      await dispatch(fetchAllLeaveRequestsCount())

      // Close the sheet after success (optional)
      setIsOpen(false);
    } catch (error: any) {
      console.error("Caught error:", error);

      toast({
        variant: "destructive",
        title: error.message || "Error",
        description: error.error || "An unknown error occurred",
      })
    } finally {
      setIsProcessing(false);
    }

  }


  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline">View</Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg flex flex-col h-full">
        <SheetHeader>
          <SheetTitle className="text-2xl font-semibold">
            Leave Request Details
          </SheetTitle>
          <SheetDescription>View details of leave request.</SheetDescription>
        </SheetHeader>

        <Separator />

        <div className="flex flex-col gap-6 flex-grow overflow-y-auto">
          {/* Content goes here */}
          <div className="font-bold text-lg">Leave Request #{request.id}</div>

          <div className="flex flex-wrap gap-10 items-start">
            <Detail title="Status">
              <ApprovalTypeTag status={request.status} />
            </Detail>
            <Detail title="Type">
              <span className="font-semibold">
                {capitalizeFirstLetter(request.type)}
              </span>
            </Detail>
            <Detail
              title={`${request.status === ApprovalType.PENDING
                ? "Requested"
                : request.status === ApprovalType.APPROVED
                  ? "Approved"
                  : request.status === ApprovalType.REJECTED
                    ? "Rejected"
                    : ""
                } On`}
            >
              <span className="font-semibold">
                {request.status === ApprovalType.PENDING
                  ? formatDateLocale(request.createdAt)
                  : formatDateLocale(request.updatedAt)}
              </span>
            </Detail>
          </div>

          <div className="flex flex-wrap gap-10 items-start">
            <Detail title="From Date">
              <span className="font-semibold">
                {formatDateLocale(request.fromDate)}{" "}
                {request.fromDateIsAm ? "(AM)" : "(PM)"}
              </span>
            </Detail>
            <Detail title="To Date">
              <span className="font-semibold">
                {formatDateLocale(request.toDate)}{" "}
                {request.toDateIsAm ? "(AM)" : "(PM)"}
              </span>
            </Detail>
            <Detail title="Duration">
              <span className="font-semibold">{request.days} days</span>
            </Detail>
          </div>

          <div>
            <Detail title="Requested By">
              <UserSnippet
                fullName={request.user.fullName}
                email={request.user.email}
                id={request.user.id}
              />
            </Detail>
          </div>

          <div>
            <Detail title="Reason">
              <div className="border border-gray-200 w-full p-4 rounded-md text-gray-600 min-h-[100px] max-h-[200px] overflow-y-auto">
                {request.reason ? (
                  request.reason
                ) : (
                  <div className="italic text-gray-400">No reason stated.</div>
                )}
              </div>
            </Detail>
          </div>
          <div>
            <Detail title="Attachment">
              {imageSignedUrl ?
                <div className="w-full h-full flex justify-center items-center group relative overflow-hidden rounded">
                  {isPdf ?
                    <iframe
                      src={`${imageSignedUrl}#toolbar=0`}
                      title="PDF attachment"
                      className="w-full h-[300px] border rounded"
                    />
                    : <img
                      src={imageSignedUrl}
                      alt="Attached file"
                      className="max-w-full max-h-[300px] object-contain transition-transform duration-300 group-hover:scale-105"
                    />}


                  <a
                    href={imageSignedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 right-2 bg-white text-black px-3 py-1 rounded shadow hover:bg-gray-100 transition-opacity opacity-0 group-hover:opacity-100"
                  >
                    <IoOpenOutline />
                  </a>
                </div>
                : <div className="italic text-gray-400">No image uploaded.</div>}
            </Detail>
          </div>
        </div>

        {request.status === ApprovalType.PENDING && (
          <div className="flex flex-col gap-4 mt-auto sticky bottom-0 bg-white pb-4">
            <Separator />
            <SheetFooter className="w-full flex flex-col sm:flex-row sm:justify-between gap-4 mt-auto items-center">
              <p className="text-sm text-gray-500">
                Note that this action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={isProcessing}
                >
                  {isProcessing ? "Processing..." : "Reject"}
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={isProcessing}
                >
                  {isProcessing ? "Processing..." : "Approve"}
                </Button>
              </div>
            </SheetFooter>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default ApprovalSheet;

export const Detail = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-gray-500 text-sm">{title}</div>
      {children}
    </div>
  );
};